import React, { useEffect } from 'react'
import ImmutablePropTypes from 'react-immutable-proptypes'
import PropTypes from 'prop-types'
import compose from 'recompose/compose'
import { connect as connectRedux } from 'react-redux'
import _get from 'lodash/get'
import _startsWith from 'lodash/startsWith'
import _endsWith from 'lodash/endsWith'
import _replace from 'lodash/replace'
import { List, Map } from 'immutable'
import { get as getConfig } from '../../../config/index'
import { getBoundActions } from '../../../actions/index'
import { GIFT_SUBSCRIPTION_STATE } from '../../../services/gift/index'
import { areEntitlementsPurchaseOnlyOrEcommerce } from '../../../services/user/index'

// Services
import {
  format as formatDateTime,
  getDateTime,
  ordinalDateFormatingi18n,
} from '../../../services/date-time/index'
import {
  URL_APPLE_ITUNES_MANAGE_SUBSCRIPTION,
  URL_GOOGLE_PAY_MANAGE_SUBSCRIPTION,
  URL_ROKU_MANAGE_SUBSCRIPTION,
  URL_ACCOUNT_CREATION,
  URL_PAYPAL_RECURRING_MANAGE_SUBSCRIPTION,
  URL_ANDROID_PAY_MANAGE_SUBSCRIPTION,
  URL_AMAZON_MANAGE_SUBSCRIPTION,
  URL_FMTV_HELP_DESK,
  URL_HELP_CENTER,
} from '../../../services/url/constants'
import {
  getEmailCustomerServiceUrl,
} from '../../../services/url/index'
import {
  USER_ACCOUNT_CANCELLED,
  ACCOUNT_TYPE_DEFAULT,
  ACCOUNT_TYPE_ITUNES,
  ACCOUNT_TYPE_GOOGLE,
  ACCOUNT_TYPE_ROKU,
  ACCOUNT_TYPE_AMAZON,
  ACCOUNT_TYPE_GCSI,
  ACCOUNT_TYPE_ZUORA,
  ACCOUNT_TYPE_ANDROID_PAY,
  ACCOUNT_TYPE_SECONDARY_PROFILE,
  ACCOUNT_TYPE_TRIAL,
  ACCOUNT_TYPE_PAYPAL_RECURRING,
  ACCOUNT_TYPE_FMTV_COMP,
  ACCOUNT_TYPE_FMTV_GIFT,
  ACCOUNT_TYPE_FMTV,
  ACCOUNT_TYPE_AI_ONLY,
  ACCOUNT_TYPE_UNIFYD_FREE_ACCESS,
  ACCOUNT_TYPE_UNIFYD_MIGRATION,
  ACCOUNT_TYPE_UNIFYD_PAYPAL_COMP,
  hasScheduledOrActivePause,
  userHasPaymentError,
  USER_ACCOUNT_ACTIVE,
  USER_ACCOUNT_PENDING,
} from '../../../services/user-account/index'
import { isEntitled } from '../../../services/subscription/index'
import { selectMostRecentEligibleEntitlementFromUser } from '../../../services/user/selectors'
import { TYPE_LOGIN } from '../../../services/dialog/index'
import { getPrimary } from '../../../services/languages/index'
import { getAuthIsLoggedIn } from '../../../services/auth/index'
import { useGetPlanDataBySku } from '../../../services/plans/hooks'
import {
  PRICE_REPLACEMENT_TYPE_PLAN,
  PRICE_REPLACEMENT_TYPE_NEXT_PLAN,
} from '../../../services/plans/utils'

// Components
import { connect as connectPage, PAGE_SEO_TYPE_LOCATION } from '../../Page/connect'
import { connect as connectStaticText } from '../../StaticText/connect'
import { H2, H3, HEADING_TYPES } from '../../Heading/index'
import ConstructionMessage from '../../ConstructionMessage/index'
import Link, { TARGET_BLANK } from '../../Link/index'
import Button, { TYPES, SIZES } from '../../Button/index'
import MyAccountDetails from '../MyAccountDetails/index'
import MyAccountMessages, { top, bottom } from '../MyAccountMessages/index'
import Sherpa, { TYPE_LARGE } from '../../Sherpa/index'
import Support from '../../Support/index'
import VerticalNavigation, { ACCOUNT } from '../../VerticalNavigation/index'
import IconV2, { ICON_TYPES } from '../../Icon.v2/index'

const config = getConfig()
const planGraphQLActive = !!_get(config, ['features', 'checkout', 'planGraphQL'])

// -------------------- MY ACCOUNT V2 -----------------------

const renderCustomerServiceLink = (props) => {
  const { staticText } = props
  const currentDate = new Date()
  const currentDateTimeStamp = getDateTime(currentDate)
  const januaryFirst2020TimeStamp = 1577836801000
  const switchOverDate = currentDateTimeStamp > januaryFirst2020TimeStamp

  return (
    <div className="my-account-V2__customer-service">
      <p className="my-account-V2__customer-service--text">
        {staticText.getIn(['data', 'contactCustomerService'])}
        <span className="my-account-V2__customer-service--link">
          {
            switchOverDate
              ? <Link
                to={URL_HELP_CENTER}
                target={TARGET_BLANK}
                className="my-account-V2__rejoin-link--text"
              >
                {` ${staticText.getIn(['data', 'pleaseClickHere'])}`}
              </Link>
              : <Link
                to={URL_FMTV_HELP_DESK}
                target={TARGET_BLANK}
                className="my-account-V2__rejoin-link--text"
              >
                {` ${staticText.getIn(['data', 'pleaseClickHere'])}`}
              </Link>
          }
        </span>
      </p>
    </div>
  )
}

const renderSecondaryProfile = (props) => {
  // renderSecondaryProfile is used for both current ops AND V2
  const { staticText } = props
  return (
    <div className="my-account-V2__content">
      <H2 className="my-account-V2__title">{staticText.getIn(['data', 'yourPlan'])}</H2>
      <p className="my-account-V2__child-profile-body1">{staticText.getIn(['data', 'childProfile1'])}</p>
      <p className="my-account-V2__child-profile-body2">{staticText.getIn(['data', 'childProfile2'])}</p>
    </div>
  )
}

const renderFreeTrial = (props, planData = List()) => {
  const {
    auth, user, staticText, userAccount, history, location,
  } = props
  const scheduledOrActivePause = hasScheduledOrActivePause(userAccount)

  return (
    <div className="my-account-V2__content">
      <MyAccountMessages
        userAccount={userAccount}
        region={top}
        scheduledOrActivePause={scheduledOrActivePause}
      />
      <H2 className="my-account-V2__title">{staticText.getIn(['data', 'yourPlan'])}</H2>
      <MyAccountDetails
        auth={auth}
        userAccount={userAccount}
        user={user}
        history={history}
        location={location}
        planData={planData}
      />
    </div>
  )
}

const renderFmtv = (props) => {
  const { staticText, userAccount } = props
  const currentDate = new Date()
  const scheduledOrActivePause = hasScheduledOrActivePause(userAccount)
  const currentDateTimeStamp = getDateTime(currentDate)
  const billingAccountId = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'billingAccountId'])
  const fmtvBillingMigrationCompletionDate = _get(config, ['features', 'userAccount', 'fmtvMigrationDate'])

  if (!billingAccountId && (currentDateTimeStamp >= fmtvBillingMigrationCompletionDate)) {
    return (
      <div className="my-account-V2__content">
        <H2 className="my-account-V2__title">{staticText.getIn(['data', 'yourPlan'])}</H2>
        <div className="my-account-V2__rejoin">
          <div className="my-account-V2__rejoin-text">
            <span className="my-account-V2__rejoin-text-welcome">{ staticText.getIn(['data', 'welcomeBack']) }</span>
            <p className="my-account-V2__rejoin-text-rejoin">{ staticText.getIn(['data', 'rejoin']) }</p>
          </div>
          <div className="my-account-V2__rejoin-link">
            <Link
              to={URL_ACCOUNT_CREATION}
              className="my-account-V2__rejoin-link--text"
            >
              { staticText.getIn(['data', 'reactivate']) }
            </Link>
            <IconV2
              className="my-account-V2__arrow-right"
              type={ICON_TYPES.CHEVRON_RIGHT}
            />
          </div>
        </div>
        <MyAccountMessages
          userAccount={userAccount}
          region={bottom}
          scheduledOrActivePause={scheduledOrActivePause}
        />
        { renderCustomerServiceLink(props) }
      </div>
    )
  }

  return (
    <div className="my-account-V2__content">
      <H2 className="my-account-V2__title">{staticText.getIn(['data', 'yourPlan'])}</H2>
      <div className="my-account-V2__rejoin">
        <div className="my-account-V2__rejoin-text">
          <span className="my-account-V2__rejoin-text-welcome">{ staticText.getIn(['data', 'welcomeToGaia']) }</span>
          <p className="my-account-V2__rejoin-text-rejoin">{ staticText.getIn(['data', 'accountTransferred']) }</p>
        </div>
      </div>
      { renderCustomerServiceLink(props) }
    </div>
  )
}

const renderFmtvGift = (props) => {
  const { staticText, page, user } = props
  const mostRecentEntitlement = selectMostRecentEligibleEntitlementFromUser(user)
  const endStr = mostRecentEntitlement ? mostRecentEntitlement.get('end') : ''
  const entitlementEnd = endStr ? parseInt(endStr, 10) * 1000 : 0
  const locale = page.get('locale')
  const dateFormatString = _endsWith(locale, 'US') ? 'M/D/YY' : 'YY/M/D'
  const formattedEndDate = formatDateTime(entitlementEnd, locale, dateFormatString)

  return (
    <div className="my-account-V2__content">
      <H2 className="my-account-V2__title">{staticText.getIn(['data', 'yourPlan'])}</H2>
      <div className="my-account-V2__rejoin">
        <div className="my-account-V2__rejoin-text">
          <span className="my-account-V2__rejoin-text-welcome">{ staticText.getIn(['data', 'welcomeToGaia']) }</span>
          <p className="my-account-V2__alternate-body--upper">{ staticText.getIn(['data', 'fmtvGiftBody1']) }<span>{` ${formattedEndDate}.`}</span></p>
          <p className="my-account-V2__alternate-body--lower">{ staticText.getIn(['data', 'continueEnjoying']) }</p>
        </div>
        <div className="my-account-V2__rejoin-link">
          <Link
            to={URL_ACCOUNT_CREATION}
            className="my-account-V2__rejoin-link--text"
          >
            { staticText.getIn(['data', 'choosePlan']) }
          </Link>
          <IconV2
            className="my-account-V2__arrow-right"
            type={ICON_TYPES.CHEVRON_RIGHT}
          />
        </div>
      </div>
      { renderCustomerServiceLink(props) }
    </div>
  )
}

const renderFmtvComp = (props) => {
  const { staticText, page, user } = props
  const mostRecentEntitlement = selectMostRecentEligibleEntitlementFromUser(user)
  const endStr = mostRecentEntitlement ? mostRecentEntitlement.get('end') : ''
  const entitlementEnd = endStr ? parseInt(endStr, 10) * 1000 : 0
  const locale = page.get('locale')
  const dateFormatString = _endsWith(locale, 'US') ? 'M/D/YY' : 'YY/M/D'
  const formattedEndDate = formatDateTime(entitlementEnd, locale, dateFormatString)

  return (
    <div className="my-account-V2__content">
      <H2 className="my-account-V2__title">{staticText.getIn(['data', 'yourPlan'])}</H2>
      <div className="my-account-V2__rejoin">
        <div className="my-account-V2__rejoin-text">
          <span className="my-account-V2__rejoin-text-welcome">{ staticText.getIn(['data', 'welcomeToGaia']) }</span>
          <p className="my-account-V2__alternate-body--upper">{ staticText.getIn(['data', 'fmtvGiftBody1']) }<span>{` ${formattedEndDate}.`}</span></p>
          <p className="my-account-V2__alternate-body--lower">{ staticText.getIn(['data', 'continueEnjoying']) }</p>
        </div>
        <div className="my-account-V2__rejoin-link">
          <Link
            to={URL_ACCOUNT_CREATION}
            className="my-account-V2__rejoin-link--text"
          >
            { staticText.getIn(['data', 'choosePlan']) }
          </Link>
          <IconV2
            className="my-account-V2__arrow-right"
            type={ICON_TYPES.CHEVRON_RIGHT}
          />
        </div>
      </div>
      { renderCustomerServiceLink(props) }
    </div>
  )
}

const renderPaypalRecurring = (props) => {
  const { staticText, auth, user } = props
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const userIsEntitled = isEntitled(auth.get('subscriptions', List()))
  return (
    <div className="my-account-V2__content">
      <H2 className="my-account-V2__title">{staticText.getIn(['data', 'yourPlan'])}</H2>
      <div className="my-account-V2__rejoin">
        {
          userIsEntitled
            ? <React.Fragment>
              <div className="my-account-V2__rejoin-text">
                <span className="my-account-V2__rejoin-text-welcome">{ staticText.getIn(['data', 'welcomeToGaia']) }</span>
                <p className="my-account-V2__alternate-body--upper">{ staticText.getIn(['data', 'paypalRecurringBody1']) }</p>
                <p className="my-account-V2__alternate-body--lower">
                  { staticText.getIn(['data', 'paypalRecurringBody2']) } { staticText.getIn(['data', 'paypalRecurringBody3']) }
                  <Link
                    to={getEmailCustomerServiceUrl(userLanguage)}
                    target={TARGET_BLANK}
                    className="my-account-V2__alternate-link--text"
                  >
                    {staticText.getIn(['data', 'customerSupport'])}
                  </Link>
                  { staticText.getIn(['data', 'team']) }
                </p>
              </div>
              <div className="my-account-V2__rejoin-link">
                <Link
                  to={URL_PAYPAL_RECURRING_MANAGE_SUBSCRIPTION}
                  target={TARGET_BLANK}
                  className="my-account-V2__alternate-link--text"
                >
                  { staticText.getIn(['data', 'manageInPaypal']) }
                </Link>
                <IconV2
                  className="my-account-V2__arrow-right"
                  type={ICON_TYPES.CHEVRON_RIGHT}
                />
              </div>
            </React.Fragment>
            : <React.Fragment>
              <div className="my-account-V2__rejoin-text">
                <span className="my-account-V2__rejoin-text-welcome">{ staticText.getIn(['data', 'welcomeToGaia']) }</span>
                <p className="my-account-V2__alternate-body--upper">{ staticText.getIn(['data', 'fmtvGiftBody2']) }</p>
              </div>
              <div className="my-account-V2__rejoin-link">
                <Link
                  to={URL_ACCOUNT_CREATION}
                  className="my-account-V2__rejoin-link--text"
                >
                  { staticText.getIn(['data', 'reactivate']) }
                </Link>
                <IconV2
                  className="my-account-V2__arrow-right"
                  type={ICON_TYPES.CHEVRON_RIGHT}
                />
              </div>
            </React.Fragment>
        }
      </div>
    </div>
  )
}

const renderAndroidPay = (props) => {
  const { staticText, auth, user } = props
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const userIsEntitled = isEntitled(auth.get('subscriptions', List()))
  return (
    <div className="my-account-V2__content">
      <H2 className="my-account-V2__title">{staticText.getIn(['data', 'yourPlan'])}</H2>
      <div className="my-account-V2__rejoin">
        {
          userIsEntitled
            ? <React.Fragment>
              <div className="my-account-V2__rejoin-text">
                <span className="my-account-V2__rejoin-text-welcome">{ staticText.getIn(['data', 'welcomeToGaia']) }</span>
                <p className="my-account-V2__alternate-body--upper">{ staticText.getIn(['data', 'androidPayBody1']) }</p>
                <p className="my-account-V2__alternate-body--lower">
                  { staticText.getIn(['data', 'androidPayBody2']) } { staticText.getIn(['data', 'androidPayBody3']) }
                  <Link
                    to={getEmailCustomerServiceUrl(userLanguage)}
                    target={TARGET_BLANK}
                    className="my-account-V2__alternate-link--text"
                  > {staticText.getIn(['data', 'customerSupport'])} </Link> { staticText.getIn(['data', 'team']) }
                </p>
              </div>
              <div className="my-account-V2__rejoin-link">
                <Link
                  to={URL_ANDROID_PAY_MANAGE_SUBSCRIPTION}
                  target={TARGET_BLANK}
                  className="my-account-V2__alternate-link--text"
                >
                  { staticText.getIn(['data', 'manageInAndroidPay']) }
                </Link>
                <IconV2
                  className="my-account-V2__arrow-right"
                  type={ICON_TYPES.CHEVRON_RIGHT}
                />
              </div>
            </React.Fragment>
            : <React.Fragment>
              <div className="my-account-V2__rejoin-text">
                <span className="my-account-V2__rejoin-text-welcome">{ staticText.getIn(['data', 'welcomeToGaia']) }</span>
                <p className="my-account-V2__alternate-body--upper">{ staticText.getIn(['data', 'fmtvGiftBody2']) }</p>
              </div>
              <div className="my-account-V2__rejoin-link">
                <Link
                  to={URL_ACCOUNT_CREATION}
                  className="my-account-V2__rejoin-link--text"
                >
                  { staticText.getIn(['data', 'reactivate']) }
                </Link>
                <IconV2
                  className="my-account-V2__arrow-right"
                  type={ICON_TYPES.CHEVRON_RIGHT}
                />
              </div>
            </React.Fragment>
        }
      </div>
    </div>
  )
}

const renderUnifydFreeAccess = (props) => {
  const { staticText } = props

  return (
    <div className="my-account-V2__content">
      <H2 className="my-account-V2__title">{staticText.getIn(['data', 'welcomeToGaia'])}</H2>
      <div className="my-account-V2__default">
        <p className="my-account-V2__default-text">
          { staticText.getIn(['data', 'unifydFreeAccessBodyPhase2']) }
        </p>
      </div>
    </div>
  )
}

const renderUnifydMigration = (props) => {
  const { staticText } = props

  return (
    <div className="my-account-V2__content">
      <H2 className="my-account-V2__title">
        {staticText.getIn(['data', 'unifydImportLockdownTitle'])}
      </H2>
      <div className="my-account-V2__default">
        <p className="my-account-V2__default-text">
          {staticText.getIn(['data', 'unifydImportLockdownBody'])}
        </p>
      </div>
    </div>
  )
}

const renderUnifydPaypalComp = (props) => {
  const { staticText, user } = props
  const mostRecentEntitlement = selectMostRecentEligibleEntitlementFromUser(user)
  const endDate = mostRecentEntitlement ? mostRecentEntitlement.get('end', '') : ''

  return (
    <React.Fragment>
      <div className="my-account-V2__alternate-desription">
        <H3 className="my-account-V2__alternate-title">
          {staticText.getIn(['data', 'welcomeToGaia'])}
        </H3>
        <p className="my-account-V2__alternate-body--upper">
          { endDate
            ? _replace(staticText.getIn(['data', 'unifydPaypalCompBody']), /\$\{\s?endDate\s?\}/g, endDate)
            : staticText.getIn(['data', 'unifydPaypalCompBodyNoDate'])
          }
        </p>
      </div>
      <div className="my-account-V2__alternate-link">
        <Link
          to={URL_ACCOUNT_CREATION}
          className="my-account-V2__alternate-link--text"
        >
          {staticText.getIn(['data', 'choosePlan'])}
        </Link>
        <IconV2
          className="my-account-V2__arrow-right"
          type={ICON_TYPES.CHEVRON_RIGHT}
        />
      </div>
    </React.Fragment>
  )
}

const renderDefaultV2 = (
  props,
  planData = List(),
  nextPlanData = List(),
  futurePlanData = List(),
) => {
  const {
    userAccount,
    auth,
    user,
    staticText,
    history,
    location,
    page,
  } = props
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const scheduledOrActivePause = hasScheduledOrActivePause(userAccount)
  const userAccountDetails = userAccount.getIn(['details', 'data', 'billing', 'subscriptions'])
  const billingAccountId = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'billingAccountId'])
  const giftSubscriptionStatus = userAccount.getIn(['details', 'data', 'gift', 'status'])
  const giftSubscriptionActive = giftSubscriptionStatus === GIFT_SUBSCRIPTION_STATE.ACTIVE
  const giftSubscriptionInactive = giftSubscriptionStatus === GIFT_SUBSCRIPTION_STATE.INACTIVE
  const creationSource = user.getIn(['data', 'creation_source'])
  const creationSourceFMTV = _startsWith(creationSource, ACCOUNT_TYPE_FMTV, 0)
  const status = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'status'])
  const cancelled = status === USER_ACCOUNT_CANCELLED
  const activeOrPending = status === USER_ACCOUNT_ACTIVE || status === USER_ACCOUNT_PENDING
  const activateOrReactivate = cancelled ? staticText.getIn(['data', 'reactivate']) : staticText.getIn(['data', 'giftSubscriptionActivateLink'])
  const billingSubscriptionsStatusCode = userAccount.get('billingSubscriptionsStatusCode')
  const userIsEntitled = isEntitled(auth.get('subscriptions', List()))
  const entitlementsPurchaseOnlyOrEcommerce = areEntitlementsPurchaseOnlyOrEcommerce(user.getIn(['data', 'userEntitlements'], List()) || List())
  const paymentError = userHasPaymentError({ userAccount })
  const mostRecentEntitlement = selectMostRecentEligibleEntitlementFromUser(user) || Map()
  const zuoraEntitledNoPlan = userAccount.get('accountType') === ACCOUNT_TYPE_ZUORA
    && !userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan'])
    && mostRecentEntitlement.size > 0

  // if we are being rate limited by Zuora
  if (billingSubscriptionsStatusCode === 429) {
    return (
      <div className="my-account-V2__content">
        <H2
          className="my-account-V2__error-title"
          as={HEADING_TYPES.H4}
        >
          {staticText.getIn(['data', 'errorTitle'])}
        </H2>
        <div className="my-account-V2__error-content">
          <p className="my-account-v2__error-text">
            {`${staticText.getIn(['data', 'errorText'])} `}
            <Link
              to={URL_HELP_CENTER}
              target={TARGET_BLANK}
              className="my-account-V2__cs-link"
            >
              {staticText.getIn(['data', 'errorLink'])}
            </Link>
            .
          </p>
        </div>
        <div className="my-account-v2__error-button">
          <Button
            type={TYPES.PRIMARY}
            size={SIZES.XSMALL}
            onClick={() => global.location.reload()}
          >
            {staticText.getIn(['data', 'refresh'])}
          </Button>
        </div>
      </div>
    )
  }

  if (!billingAccountId && creationSourceFMTV) {
    return (
      <div className="my-account-V2__content">
        <H2 className="my-account-V2__title">{staticText.getIn(['data', 'yourPlan'])}</H2>
        <div className="my-account-V2__rejoin">
          <div className="my-account-V2__rejoin-text">
            <span className="my-account-V2__rejoin-text-welcome">{ staticText.getIn(['data', 'welcomeBack']) }</span>
            <p className="my-account-V2__rejoin-text-rejoin">{ staticText.getIn(['data', 'rejoin']) }</p>
          </div>
          <div className="my-account-V2__rejoin-link">
            <Link
              to={URL_ACCOUNT_CREATION}
              className="my-account-V2__rejoin-link--text"
            >
              { staticText.getIn(['data', 'reactivate']) }
            </Link>
            <IconV2
              className="my-account-V2__arrow-right"
              type={ICON_TYPES.CHEVRON_RIGHT}
            />
          </div>
        </div>
      </div>
    )
  }

  if (
    (!billingAccountId && giftSubscriptionInactive && !userIsEntitled)
      || (billingAccountId
         && !userIsEntitled
         && !userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'previousPlan'])
         && !userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan'])
         && !(userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'planHistory'], List()) || List()).size)
  ) {
    return (
      <div className="my-account-V2__content">
        <H2 className="my-account-V2__title">{staticText.getIn(['data', 'yourPlan'])}</H2>
        <div className="my-account-V2__rejoin">
          <div className="my-account-V2__rejoin-text">
            <span className="my-account-V2__rejoin-text-welcome">{staticText.getIn(['data', 'welcomeBack'])}</span>
            <p className="my-account-V2__rejoin-text-rejoin">{staticText.getIn(['data', 'rejoin'])}</p>
          </div>
          <div className="my-account-V2__rejoin-link">
            <Link
              to={URL_ACCOUNT_CREATION}
              className="my-account-V2__rejoin-link--text"
            >
              {staticText.getIn(['data', 'reactivate'])}
            </Link>
            <IconV2
              className="my-account-V2__arrow-right"
              type={ICON_TYPES.CHEVRON_RIGHT}
            />
          </div>
        </div>
      </div>
    )
  }

  if (
    (entitlementsPurchaseOnlyOrEcommerce && !paymentError && !scheduledOrActivePause)
      || (zuoraEntitledNoPlan
        && !paymentError
        && !scheduledOrActivePause
        && !giftSubscriptionActive)
  ) {
    return (
      <div className="my-account-V2__content">
        <H2 className="my-account-V2__title">{staticText.getIn(['data', 'yourPlan'])}</H2>
        <div className="my-account-V2__purchase-only-or-ecommerce">
          <div className="my-account-V2__purchase-only-or-ecommerce-text">
            {staticText.getIn(['data', 'purchaseOnlyOrEcommerceText'])}
          </div>
          <div className="my-account-V2__purchase-only-or-ecommerce-link">
            <Link
              to={URL_ACCOUNT_CREATION}
              className="my-account-V2__purchase-only-or-ecommerce-link--text"
            >
              { staticText.getIn(['data', 'purchaseOnlyOrEcommerceLink']) }
            </Link>
            <IconV2
              className="my-account-V2__arrow-right"
              type={ICON_TYPES.CHEVRON_RIGHT}
            />
          </div>
        </div>
      </div>
    )
  }

  if (!billingAccountId && !giftSubscriptionActive) {
    return (
      <div className="my-account-V2__content">
        <H2 className="my-account-V2__title">{staticText.getIn(['data', 'yourPlan'])}</H2>
        <div className="my-account-V2__default">
          <p className="my-account-V2__default-text">
            {`${staticText.getIn(['data', 'defaultMessage'])} `}
            <Link
              to={getEmailCustomerServiceUrl(userLanguage)}
              target={TARGET_BLANK}
            >
              {`${staticText.getIn(['data', 'defaultCta'])}.`}
            </Link>
          </p>
        </div>
      </div>
    )
  }

  if (giftSubscriptionActive && !activeOrPending) {
    const giftSubscriptionEndDate = userAccount.getIn(['details', 'data', 'gift', 'paidThroughDate'])
    const locale = page.get('locale')
    const dateFormatString = ordinalDateFormatingi18n(userLanguage)
    const formattedEndDate = formatDateTime(giftSubscriptionEndDate, locale, dateFormatString)
    return (
      <div className="my-account-V2__content">
        <H2 className="my-account-V2__title">{staticText.getIn(['data', 'yourPlan'])}</H2>
        <div className="my-account-V2__gift-activate">
          <div className="my-account-V2__gift-activate-text">
            <span className="my-account-V2__gift-activate-title">{ staticText.getIn(['data', 'giftSubscription']) }</span>
            <p className="my-account-V2__gift-activate-copy">
              {/* eslint-disable-next-line no-template-curly-in-string */}
              {_replace(staticText.getIn(['data', 'giftSubscriptionActivateCopy']), '${ activateOrReactivate }', activateOrReactivate)}
            </p>
          </div>
          <div className="my-account-V2__gift-activate-link">
            <Link
              to={URL_ACCOUNT_CREATION}
              className="my-account-V2__gift-activate-link--text"
            >
              { cancelled ? staticText.getIn(['data', 'reactivate']) : staticText.getIn(['data', 'giftSubscriptionActivateLink']) }
            </Link>
            <IconV2
              className="my-account-V2__arrow-right"
              type={ICON_TYPES.CHEVRON_RIGHT}
            />
          </div>
        </div>
        <div className="my-account-V2__gift-activate-end-date">
          {`${staticText.getIn(['data', 'giftSubscriptionEnds'])} ${formattedEndDate}`}
        </div>
      </div>
    )
  }

  if (userAccountDetails) {
    return (
      <div className="my-account-V2__content">
        <MyAccountMessages
          userAccount={userAccount}
          region={top}
          scheduledOrActivePause={scheduledOrActivePause}
        />
        <H2 className="my-account-V2__title">{staticText.getIn(['data', 'yourPlan'])}</H2>
        <MyAccountDetails
          auth={auth}
          userAccount={userAccount}
          user={user}
          history={history}
          location={location}
          planData={planData}
          nextPlanData={nextPlanData}
          futurePlanData={futurePlanData}
        />
      </div>
    )
  }

  return (
    <div className="my-account-V2__content">
      <H2 className="my-account-V2__title">{staticText.getIn(['data', 'yourPlan'])}</H2>
      { staticText.getIn(['data', 'genericLink']) }
    </div>
  )
}

export const renderArchitectPlus = (props, planData, nextPlanData, futurePlanData) => {
  const {
    staticText,
    user,
    auth,
    userAccount,
    history,
    location,
  } = props
  const userAccountDetails = userAccount.getIn(['details', 'data', 'billing', 'subscriptions'])

  if (userAccountDetails) {
    return (
      <div className="my-account-V2__content">
        <H2 className="my-account-V2__title">{staticText.getIn(['data', 'yourPlan'])}</H2>
        <MyAccountDetails
          auth={auth}
          userAccount={userAccount}
          user={user}
          history={history}
          location={location}
          planData={planData}
          nextPlanData={nextPlanData}
          futurePlanData={futurePlanData}
        />
      </div>
    )
  }

  return null
}

export const renderItunesV2 = (props) => {
  const { staticText, auth, user } = props
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const userIsEntitled = isEntitled(auth.get('subscriptions', List()))
  return (
    <div className="my-account-V2__alternate-container">
      {
        userIsEntitled
          ? <React.Fragment>
            <div className="my-account-V2__alternate-desription">
              <H2>{staticText.getIn(['data', 'yourPlan'])}</H2>
              <H3 className="my-account-V2__alternate-title">{ staticText.getIn(['data', 'appleTitle']) }</H3>
              <p className="my-account-V2__alternate-body--upper">{ staticText.getIn(['data', 'appleBody1']) }</p>
              <p className="my-account-V2__alternate-body--lower">
                { staticText.getIn(['data', 'appleBody2']) } { staticText.getIn(['data', 'appleBody3']) }
                <Link
                  to={getEmailCustomerServiceUrl(userLanguage)}
                  target={TARGET_BLANK}
                  className="my-account-V2__alternate-link--text"
                >
                  {staticText.getIn(['data', 'customerSupport'])}
                </Link>
                { staticText.getIn(['data', 'team']) }
              </p>
            </div>
            <div className="my-account-V2__alternate-link">
              <Link
                to={URL_APPLE_ITUNES_MANAGE_SUBSCRIPTION}
                target={TARGET_BLANK}
                className="my-account-V2__alternate-link--text"
              >
                { staticText.getIn(['data', 'appleLink']) }
              </Link>
              <IconV2
                className="my-account-V2__arrow-right"
                type={ICON_TYPES.CHEVRON_RIGHT}
              />
            </div>
          </React.Fragment>
          : <React.Fragment>
            <div className="my-account-V2__rejoin-text">
              <H2>{staticText.getIn(['data', 'yourPlan'])}</H2>
              <span className="my-account-V2__rejoin-text-welcome">{ staticText.getIn(['data', 'welcomeToGaia']) }</span>
              <p className="my-account-V2__alternate-body--lower">{ staticText.getIn(['data', 'fmtvGiftBody2']) }</p>
            </div>
            <div className="my-account-V2__rejoin-link">
              <Link
                to={URL_ACCOUNT_CREATION}
                className="my-account-V2__rejoin-link--text"
              >
                { staticText.getIn(['data', 'reactivate']) }
              </Link>
              <IconV2
                className="my-account-V2__arrow-right"
                type={ICON_TYPES.CHEVRON_RIGHT}
              />
            </div>
          </React.Fragment>
      }
    </div>
  )
}

export const renderGoogle = (props) => {
  const { staticText, auth, user } = props
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const userIsEntitled = isEntitled(auth.get('subscriptions', List()))
  return (
    <div className="my-account-V2__alternate-container">
      {
        userIsEntitled
          ? <React.Fragment>
            <div className="my-account-V2__alternate-desription">
              <H2>{staticText.getIn(['data', 'yourPlan'])}</H2>
              <H3 className="my-account-V2__alternate-title">{ staticText.getIn(['data', 'googleTitle']) }</H3>
              <p className="my-account-V2__alternate-body--upper">{ staticText.getIn(['data', 'googleBody1']) }</p>
              <p className="my-account-V2__alternate-body--lower">
                { staticText.getIn(['data', 'googleBody2']) } { staticText.getIn(['data', 'googleBody3']) }
                <Link
                  to={getEmailCustomerServiceUrl(userLanguage)}
                  target={TARGET_BLANK}
                  className="my-account-V2__alternate-link--text"
                >
                  {staticText.getIn(['data', 'customerSupport'])}
                </Link>
                { staticText.getIn(['data', 'team']) }
              </p>
            </div>
            <div className="my-account-V2__alternate-link">
              <Link
                to={URL_GOOGLE_PAY_MANAGE_SUBSCRIPTION}
                target={TARGET_BLANK}
                className="my-account-V2__alternate-link--text"
              >
                { staticText.getIn(['data', 'googleLink']) }
              </Link>
              <IconV2
                className="my-account-V2__arrow-right"
                type={ICON_TYPES.CHEVRON_RIGHT}
              />
            </div>
          </React.Fragment>
          : <React.Fragment>
            <div className="my-account-V2__rejoin-text">
              <H2>{staticText.getIn(['data', 'yourPlan'])}</H2>
              <span className="my-account-V2__rejoin-text-welcome">{ staticText.getIn(['data', 'welcomeToGaia']) }</span>
              <p className="my-account-V2__alternate-body--lower">{ staticText.getIn(['data', 'fmtvGiftBody2']) }</p>
            </div>
            <div className="my-account-V2__rejoin-link">
              <Link
                to={URL_ACCOUNT_CREATION}
                className="my-account-V2__rejoin-link--text"
              >
                { staticText.getIn(['data', 'reactivate']) }
              </Link>
              <IconV2
                className="my-account-V2__arrow-right"
                type={ICON_TYPES.CHEVRON_RIGHT}
              />
            </div>
          </React.Fragment>
      }
    </div>
  )
}

export const renderRokuV2 = (props) => {
  const { staticText, auth, user } = props
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const userIsEntitled = isEntitled(auth.get('subscriptions', List()))
  return (
    <div className="my-account-V2__alternate-container">
      {
        userIsEntitled
          ? <React.Fragment>
            <div className="my-account-V2__alternate-desription">
              <H2 className="my-account-V2__alternate-title--plan">{ staticText.getIn(['data', 'yourPlan'])}</H2>
              <H3 className="my-account-V2__alternate-title">{ staticText.getIn(['data', 'rokuTitle']) }</H3>
              <p className="my-account-V2__alternate-body--upper">{ staticText.getIn(['data', 'rokuBody1']) }</p>
              <p className="my-account-V2__alternate-body--lower">
                { staticText.getIn(['data', 'rokuBody2']) } { staticText.getIn(['data', 'rokuBody3']) }
                <Link
                  to={getEmailCustomerServiceUrl(userLanguage)}
                  target={TARGET_BLANK}
                  className="my-account-V2__alternate-link--text"
                > {staticText.getIn(['data', 'customerSupport'])} </Link> { staticText.getIn(['data', 'team']) }
              </p>
            </div>
            <div className="my-account-V2__alternate-link">
              <Link
                to={URL_ROKU_MANAGE_SUBSCRIPTION}
                target={TARGET_BLANK}
                className="my-account-V2__alternate-link--text"
              >
                { staticText.getIn(['data', 'rokuLink']) }
              </Link>
              <IconV2
                className="my-account-V2__arrow-right"
                type={ICON_TYPES.CHEVRON_RIGHT}
              />
            </div>
          </React.Fragment>
          : <React.Fragment>
            <div className="my-account-V2__rejoin-text">
              <H2 className="my-account-V2__alternate-title--plan">{ staticText.getIn(['data', 'yourPlan'])}</H2>
              <span className="my-account-V2__rejoin-text-welcome">{ staticText.getIn(['data', 'welcomeToGaia']) }</span>
              <p className="my-account-V2__alternate-body--lower">{ staticText.getIn(['data', 'fmtvGiftBody2']) }</p>
            </div>
            <div className="my-account-V2__rejoin-link">
              <Link
                to={URL_ACCOUNT_CREATION}
                className="my-account-V2__rejoin-link--text"
              >
                { staticText.getIn(['data', 'reactivate']) }
              </Link>
              <IconV2
                className="my-account-V2__arrow-right"
                type={ICON_TYPES.CHEVRON_RIGHT}
              />
            </div>
          </React.Fragment>
      }
    </div>
  )
}

export const renderAmazon = (props) => {
  const { staticText, auth } = props
  const userIsEntitled = isEntitled(auth.get('subscriptions', List()))
  return (
    <div className="my-account-V2__alternate-container">
      {
        userIsEntitled
          ? <React.Fragment>
            <div className="my-account-V2__alternate-desription">
              <H2>
                {staticText.getIn(['data', 'yourPlan'])}
              </H2>
              <H3 className="my-account-V2__alternate-title">
                { staticText.getIn(['data', 'amazonTitle']) }
              </H3>
              <p className="my-account-V2__alternate-body--lower">
                { staticText.getIn(['data', 'amazonBody']) }
              </p>
            </div>
            <div className="my-account-V2__alternate-link">
              <Link
                to={URL_AMAZON_MANAGE_SUBSCRIPTION}
                target={TARGET_BLANK}
                className="my-account-V2__alternate-link--text"
              >
                { staticText.getIn(['data', 'amazonLink']) }
              </Link>
              <IconV2
                className="my-account-V2__arrow-right"
                type={ICON_TYPES.CHEVRON_RIGHT}
              />
            </div>
          </React.Fragment>
          : <React.Fragment>
            <div className="my-account-V2__rejoin-text">
              <H2>{staticText.getIn(['data', 'yourPlan'])}</H2>
              <span className="my-account-V2__rejoin-text-welcome">{ staticText.getIn(['data', 'welcomeToGaia']) }</span>
              <p className="my-account-V2__alternate-body--lower">{ staticText.getIn(['data', 'fmtvGiftBody2']) }</p>
            </div>
            <div className="my-account-V2__rejoin-link">
              <Link
                to={URL_ACCOUNT_CREATION}
                className="my-account-V2__rejoin-link--text"
              >
                { staticText.getIn(['data', 'reactivate']) }
              </Link>
              <IconV2
                className="my-account-V2__arrow-right"
                type={ICON_TYPES.CHEVRON_RIGHT}
              />
            </div>
          </React.Fragment>
      }
    </div>
  )
}

function renderContent (props, planData, nextPlanData, futurePlanData) {
  const underConstruction = _get(config, 'features.zuoraOutage')
  const { userAccount, paytrack } = props
  const userAccountProcessing = userAccount.get('billingSubscriptionsProcessing')
  const userAccountDetails = userAccount.getIn(['details', 'data', 'billing', 'subscriptions'])
  const paytrackProcessing = paytrack.get('processing')
  const paymentsProcessing = userAccount.get('billingSubscriptionPaymentsProcessing')
  const accountType = userAccount.get('accountType')

  if (underConstruction) {
    return (
      <ConstructionMessage />
    )
  }

  if (
    userAccountProcessing
    || paytrackProcessing
    || paymentsProcessing
    || !userAccountDetails
    || !accountType
  ) {
    return (
      <div className="my-account-page__sherpa-placeholder">
        <Sherpa type={TYPE_LARGE} />
      </div>
    )
  }

  switch (accountType) {
    case ACCOUNT_TYPE_PAYPAL_RECURRING:
      return renderPaypalRecurring(props)
    case ACCOUNT_TYPE_ANDROID_PAY:
      return renderAndroidPay(props)
    case ACCOUNT_TYPE_FMTV:
      return renderFmtv(props)
    case ACCOUNT_TYPE_FMTV_GIFT:
      return renderFmtvGift(props)
    case ACCOUNT_TYPE_FMTV_COMP:
      return renderFmtvComp(props)
    case ACCOUNT_TYPE_TRIAL:
      return renderFreeTrial(props, planData)
    case ACCOUNT_TYPE_SECONDARY_PROFILE:
      return renderSecondaryProfile(props)
    case ACCOUNT_TYPE_ROKU:
      return renderRokuV2(props)
    case ACCOUNT_TYPE_ITUNES:
      return renderItunesV2(props)
    case ACCOUNT_TYPE_GOOGLE:
      return renderGoogle(props)
    case ACCOUNT_TYPE_AMAZON:
      return renderAmazon(props)
    case ACCOUNT_TYPE_AI_ONLY:
      return renderArchitectPlus(props, planData, nextPlanData, futurePlanData)
    case ACCOUNT_TYPE_UNIFYD_FREE_ACCESS:
      return renderUnifydFreeAccess(props)
    case ACCOUNT_TYPE_UNIFYD_MIGRATION:
      return renderUnifydMigration(props)
    case ACCOUNT_TYPE_UNIFYD_PAYPAL_COMP:
      return renderUnifydPaypalComp(props)
    case ACCOUNT_TYPE_DEFAULT:
    case ACCOUNT_TYPE_GCSI:
    case ACCOUNT_TYPE_ZUORA:
      return renderDefaultV2(props, planData, nextPlanData, futurePlanData)
    default:
      return renderDefaultV2(props, planData, nextPlanData, futurePlanData)
  }
}

function MyAccountPage (props) {
  const {
    location,
    user,
    auth,
    userAccount,
  } = props
  const authJwt = auth.get('jwt')
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const isLoggedIn = getAuthIsLoggedIn(auth)
  // get the current plan sku, either gift or standard
  const currentPlanSku = userAccount.getIn(['details', 'data', 'gift', 'currentGift', 'planSku'])
    || userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'planSku'])
  // get the next plan sku, either gift or standard
  const nextPlanSku = userAccount.getIn(['details', 'data', 'gift', 'nextGift', 'planSku'])
    || userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'nextPlan', 'planSku'])
  const futurePlanSku = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'futurePlan', 'planSku'])

  const gqlPlanData = planGraphQLActive
    ? useGetPlanDataBySku({
      userLanguage,
      authJwt,
      planSkus: currentPlanSku,
      priceReplacementType: PRICE_REPLACEMENT_TYPE_PLAN,
      forceRefresh: true,
    })
    : Map()
  const formattedPlanData = gqlPlanData.get('plans', List()) || List()
  const gqlNextPlanData = planGraphQLActive
    ? useGetPlanDataBySku({
      userLanguage,
      authJwt,
      planSkus: nextPlanSku,
      priceReplacementType: PRICE_REPLACEMENT_TYPE_NEXT_PLAN,
      forceRefresh: true,
    })
    : Map()
  const formattedNextPlanData = gqlNextPlanData.get('plans', List()) || List()
  const gqlFuturePlanData = planGraphQLActive
    ? useGetPlanDataBySku({
      userLanguage,
      authJwt,
      planSkus: futurePlanSku,
      priceReplacementType: PRICE_REPLACEMENT_TYPE_NEXT_PLAN,
      forceRefresh: true,
    })
    : Map()
  const formattedFuturePlanData = gqlFuturePlanData.get('plans', List()) || List()

  useEffect(() => {
    const {
      setOverlayDialogVisible,
      setDialogOptions,
      setOverlayCloseOnClick,
    } = props
    // if user isn't logged on or the feature toggle is turned off
    if (!isLoggedIn) {
      setOverlayDialogVisible(TYPE_LOGIN)
      setDialogOptions(null, true)
      setOverlayCloseOnClick(false)
    }
  }, [])

  return (
    <div>
      <div className="my-account__grid">
        <VerticalNavigation location={location} navType={ACCOUNT} />
        <div className="my-account my-account__content-box my-account-V2">
          {renderContent(
            props,
            formattedPlanData || List(),
            formattedNextPlanData || List(),
            formattedFuturePlanData || List(),
          )}
        </div>
      </div>
      <div className="my-account-V2__support-container">
        <Support />
      </div>
    </div>
  )
}

MyAccountPage.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  paytrack: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  plans: ImmutablePropTypes.map.isRequired,
  userAccount: ImmutablePropTypes.map.isRequired,
  serverTime: ImmutablePropTypes.map.isRequired,
  setDialogOptions: PropTypes.func.isRequired,
  setOverlayCloseOnClick: PropTypes.func.isRequired,
  userProfiles: ImmutablePropTypes.map,
}

export default compose(
  connectPage({ seoType: PAGE_SEO_TYPE_LOCATION }),
  connectStaticText({ storeKey: 'myAccountPage' }),
  connectRedux(
    (state) => ({
      page: state.page,
      user: state.user,
      plans: state.plans,
      userAccount: state.userAccount,
      auth: state.auth,
      paytrack: state.paytrack,
      serverTime: state.serverTime,
      userProfiles: state.userProfiles,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setOverlayDialogVisible: actions.dialog.setOverlayDialogVisible,
        setDialogOptions: actions.dialog.setDialogOptions,
        setOverlayCloseOnClick: actions.dialog.setOverlayCloseOnClick,
      }
    },
  ),
)(MyAccountPage)
